import { decodeEventLog, zeroAddress, type Hex } from "viem";
import { ponsRewardNftABI } from "../../../abis/pons-reward-nft";
import { REFERRAL_RECEIPT_PROTOCOL, REFERRAL_FINALIZED_RECEIPT_PROTOCOL, type ReferralMintIdentity } from "./referral-mint";
import type { WalletTransactionReceipt } from "../../../wallet/eip1193";
import { ELYSIUM, sameIdentity } from "../../../config/elysium";
import { referralRewardRange } from "./referral-policy";

/** Only this explicitly admitted protocol is supported; ABI presence grants nothing. */
export function referralMintReceipt(receipt: WalletTransactionReceipt, expected: {
  collection: string; account: string; sourceToken: string; identity: ReferralMintIdentity; chainId?: number;
}): { tokenId: string; rewardStatus: "FINALIZED" | "PENDING_RANDOMNESS" } | null {
  const { identity } = expected;
  if (![REFERRAL_RECEIPT_PROTOCOL, REFERRAL_FINALIZED_RECEIPT_PROTOCOL].includes(identity.receiptProtocol) || receipt.status !== "0x1" ||
    !/^0x(?:0|[1-9a-f][0-9a-f]*)$/i.test(receipt.blockNumber) || !/^0x[0-9a-f]{64}$/i.test(receipt.blockHash)) return null;
  const events = receipt.logs.filter(log => same(log.address, expected.collection)).flatMap(log => {
    try { return [decodeEventLog({ abi: ponsRewardNftABI, data: log.data, topics: [...log.topics] as [Hex, ...Hex[]], strict: true })]; }
    catch { return []; }
  });
  const mints = events.filter(event => event.eventName === "PendingNftMinted" &&
    same(event.args.proofKey, identity.proofKey) && same(event.args.authorizationId, identity.authorizationId));
  if (mints.length !== 1 || mints[0]?.eventName !== "PendingNftMinted") return null;
  const mint = mints[0].args;
  if (!same(mint.wallet, expected.account) || !same(mint.sourceToken, expected.sourceToken) ||
    mint.selectedTierUsd !== BigInt(identity.milestone) || mint.mintFeeWei !== 0n ||
    !same(mint.rewardPolicyHash, identity.policyHash) || mint.rewardPolicyVersion.toString() !== identity.policyVersion) return null;
  const transfers = events.filter(event => event.eventName === "Transfer" && event.args.tokenId === mint.tokenId &&
    same(event.args.from, zeroAddress) && same(event.args.to, expected.account));
  if (transfers.length !== 1) return null;
  const rewards = events.filter(event => event.eventName === "RewardFinalized" && event.args.tokenId === mint.tokenId);
  if (rewards.length > 1) return null;
  const range = referralRewardRange(identity.milestone, identity.policyVersion, identity.policyHash,
    expected.chainId ?? (sameIdentity(expected.collection, ELYSIUM.collection) ? ELYSIUM.chainId : 4663));
  if (!range) return null;
  const reward = rewards[0];
  if (identity.receiptProtocol === REFERRAL_FINALIZED_RECEIPT_PROTOCOL && !reward) return null;
  if (reward?.eventName === "RewardFinalized" && (!same(reward.args.rewardPolicyHash, identity.policyHash) ||
    reward.args.rewardBps < range.min || reward.args.rewardBps > range.max || reward.args.bandIndex > 4)) return null;
  return { tokenId: mint.tokenId.toString(), rewardStatus: reward ? "FINALIZED" : "PENDING_RANDOMNESS" };
}
function same(a: string, b: string) { return a.toLowerCase() === b.toLowerCase(); }
